// Every edit the UI can make: clone, resolve the path, mutate, return the new
// tree. A caller assigns the result — nothing here changes what it was given.
import {
  QueryFilter,
  YukkaQuery,
} from 'src/app/shared/models/ai-search.interface';
import {
  DEFAULT_SLOP,
  QbArtifactKind,
  QbArtifactRef,
  QbGroup,
  QbJoiner,
  QbNode,
  isFreeText,
} from './query-builder.types';
import {
  cloneTree,
  isExclusionSubtree,
  isExclusionsNode,
  makeGroundedGroup,
  makeTermsGroup,
  nodeAt,
  operatorOf,
  pathKey,
  pruneEmpty,
} from './query-builder.tree';

// Adding a required group to an OR-rooted query has to wrap it: the existing
// alternatives stay alternatives, and the new group applies to all of them.
function ensureAndRoot(tree: YukkaQuery): YukkaQuery {
  if (operatorOf(tree) === 'and') return cloneTree(tree);
  const previous = cloneTree(tree);
  const referencedPortfolios = previous.referenced_portfolios ?? [];
  const referencedQueries = previous.referenced_queries ?? [];
  delete previous.referenced_portfolios;
  delete previous.referenced_queries;
  return {
    operator: 'and',
    filters: [],
    groups: [previous],
    referenced_portfolios: referencedPortfolios,
    referenced_queries: referencedQueries,
  };
}

export function addTerm(
  tree: YukkaQuery,
  path: number[],
  filter: QueryFilter,
): YukkaQuery {
  const next = cloneTree(tree);
  const node = nodeAt(next, path);
  node.filters = [...(node.filters ?? []), { ...filter }];
  return next;
}

export function removeTerm(
  tree: YukkaQuery,
  path: number[],
  index: number,
): YukkaQuery {
  const next = cloneTree(tree);
  nodeAt(next, path).filters.splice(index, 1);
  return next;
}

export function renameTerm(
  tree: YukkaQuery,
  path: number[],
  index: number,
  value: string,
): YukkaQuery {
  const next = cloneTree(tree);
  const filter = nodeAt(next, path).filters[index];
  // A guard, not a UI concern: `value` holds the resolved id for every field but
  // free text, so writing the label into it drops the match.
  if (filter && isFreeText(filter.field)) {
    filter.value = value;
    filter.name = value;
    filter.input = value;
  }
  return next;
}

// `+ group` — one more thing the article must mention.
export function addGroup(tree: YukkaQuery, filter?: QueryFilter): YukkaQuery {
  const next = ensureAndRoot(tree);
  next.groups.push(makeTermsGroup(filter ? [filter] : []));
  return next;
}

// `+ ambiguous form` — an alternative that needs corroboration. It goes beside
// the other alternatives (model.contentPath), never at the root: under an AND
// root that would make it a requirement instead of an alternative.
export function addGroundedGroup(
  tree: YukkaQuery,
  ownerPath: number[] = [],
  term = '',
): YukkaQuery {
  const next = cloneTree(tree);
  const owner = nodeAt(next, ownerPath);
  owner.groups = [...(owner.groups ?? []), makeGroundedGroup(term)];
  return next;
}

// Drop a whole group. The root's own filters are a group in the model but not a
// node in the tree, so removing that one clears the filters instead.
/**
 * Drop a whole group — everything it is made of, wherever that lives: its own
 * terms (which a merged bag spreads across several nodes), plus the context and
 * guard nodes that qualify them.
 *
 * Deleting the group's *node* is not the same thing: under an unwrapped root, or
 * in a chain, that node also owns the groups rendered below it, and removing it
 * would take them with it.
 */
export function removeGroup(tree: YukkaQuery, group: QbGroup): YukkaQuery {
  const next = cloneTree(tree);
  const terms = [
    ...group.terms,
    ...(group.context?.terms ?? []),
    ...(group.guard?.terms ?? []),
  ];

  // Highest index first, so the earlier ones stay where they are.
  const byNode = new Map<string, { path: number[]; indexes: number[] }>();
  for (const term of terms) {
    const key = pathKey(term.path);
    const entry = byNode.get(key) ?? { path: term.path, indexes: [] };
    entry.indexes.push(term.index);
    byNode.set(key, entry);
  }
  for (const { path, indexes } of byNode.values()) {
    const node = nodeAt(next, path);
    [...indexes]
      .sort((a, b) => b - a)
      .forEach((i) => (node.filters ?? []).splice(i, 1));
  }

  // An empty context or guard node is not a group of its own — and neither is a
  // bag node the terms have just left.
  pruneEmpty(next);
  return next;
}

// Split: each term of one group becomes a required group of its own. OR turns
// into AND, and the terms keep their order.
export function splitGroup(tree: YukkaQuery, path: number[]): YukkaQuery {
  if (operatorOf(tree) !== 'and') return tree;
  const next = cloneTree(tree);

  if (!path.length) {
    const filters = next.filters ?? [];
    if (filters.length < 2 || filters.some((f) => f.negated)) return tree;
    next.filters = [];
    // Root filters render before the child groups, so keep them at the front.
    next.groups = [
      ...filters.map((f) => makeTermsGroup([f])),
      ...(next.groups ?? []),
    ];
    return next;
  }

  if (path.length !== 1) return tree;
  const index = path[0];
  const node = next.groups[index];
  const filters = node?.filters ?? [];
  if (!node || (node.groups ?? []).length) return tree;
  if (filters.length < 2 || filters.some((f) => f.negated)) return tree;
  next.groups.splice(index, 1, ...filters.map((f) => makeTermsGroup([f])));
  return next;
}

// Merge: two required groups become one bag of alternatives. AND turns into OR.
export function mergeGroups(
  tree: YukkaQuery,
  pathA: number[],
  pathB: number[],
): YukkaQuery {
  if (operatorOf(tree) !== 'and') return tree;
  if (pathA.length > 1 || pathB.length > 1) return tree;
  if (!pathA.length && !pathB.length) return tree;

  // Merging is commutative, so the result must not depend on which group the
  // user invoked it from: terms always read in the order the zones show them,
  // and the root's own filters render first.
  const order = (path: number[]): number => (path.length ? path[0] + 1 : 0);
  const [earlier, later] =
    order(pathA) <= order(pathB) ? [pathA, pathB] : [pathB, pathA];

  const next = cloneTree(tree);
  const collect = (path: number[]): QueryFilter[] | null => {
    if (!path.length) return next.filters ?? [];
    const node = next.groups[path[0]];
    if (!node || (node.groups ?? []).length) return null;
    return node.filters ?? [];
  };
  const a = collect(earlier);
  const b = collect(later);
  if (!a || !b) return tree;
  if ([...a, ...b].some((f) => f.negated)) return tree;

  const merged = makeTermsGroup([...a, ...b]);
  // The root's own filters always render first, so a merge that involves them
  // lands at the front and the root is left with none.
  if (!earlier.length || !later.length) {
    const childIndex = earlier.length ? earlier[0] : later[0];
    next.filters = [];
    next.groups.splice(childIndex, 1);
    next.groups.unshift(merged);
    return next;
  }
  const [first, second] = [earlier[0], later[0]];
  next.groups.splice(second, 1);
  next.groups.splice(first, 1, merged);
  return next;
}

// An exclusion applies to the whole query, so it needs an AND root to hang off.
export function addExclusion(
  tree: YukkaQuery,
  filter: QueryFilter,
): YukkaQuery {
  const next = ensureAndRoot(tree);
  const negated = { ...filter, negated: true };

  const existing = (next.groups ?? []).findIndex((g) => isExclusionsNode(g));
  if (existing >= 0) {
    next.groups[existing].filters.push(negated);
    return next;
  }
  const rootFilters = next.filters ?? [];
  if (rootFilters.length && rootFilters.every((f) => f.negated)) {
    next.filters = [...rootFilters, negated];
    return next;
  }
  next.groups = [
    ...(next.groups ?? []),
    { operator: 'and', slop: DEFAULT_SLOP, filters: [negated], groups: [] },
  ];
  return next;
}

// --- The structure view's edits ---------------------------------------------
//
// Each one addresses a single node by path and touches nothing else. That is the
// whole point of the structure view: a shape the zones cannot draw stays
// editable without any part of the query being rewritten around it.

/** Set one node's operator. Shown literally, so nothing is normalised with it. */
export function setNodeOperator(
  tree: YukkaQuery,
  path: number[],
  operator: 'and' | 'or',
): YukkaQuery {
  const next = cloneTree(tree);
  nodeAt(next, path).operator = operator;
  return next;
}

/** Nest an empty group under one node. */
export function addChildGroup(tree: YukkaQuery, path: number[]): YukkaQuery {
  const next = cloneTree(tree);
  const node = nodeAt(next, path);
  node.groups = [...(node.groups ?? []), makeTermsGroup()];
  return next;
}

/**
 * Drop one node and everything under it. The root is the query — removing it
 * would leave nothing to edit — so that is refused.
 */
export function removeNode(tree: YukkaQuery, path: number[]): YukkaQuery {
  if (!path.length) return tree;
  const next = cloneTree(tree);
  const parent = nodeAt(next, path.slice(0, -1));
  parent.groups.splice(path[path.length - 1], 1);
  pruneEmpty(next);
  return next;
}

/**
 * The repair for `inert-exclusion`: take every negated term out of an OR bag and
 * put it where it does what its author meant — the query-wide exclusions.
 *
 * Surgical: nothing else about the query moves, so a query that was otherwise
 * fine comes back rendering exactly as before, minus the exclusions that never
 * worked. It is the one blocker that is a defect in the saved query rather than
 * a shape, which is why it is still worth offering a repair for.
 */
export function hoistInertExclusions(tree: YukkaQuery): YukkaQuery {
  const lifted: QueryFilter[] = [];

  const strip = (node: QbNode): void => {
    const filters = node.filters ?? [];
    if (operatorOf(node) === 'or' && filters.some((f) => !f.negated)) {
      node.filters = filters.filter((f) => {
        if (!f.negated) return true;
        lifted.push({ ...f });
        return false;
      });
    }
    (node.groups ?? []).forEach((child) => strip(child));
  };

  const next = cloneTree(tree);
  strip(next);
  if (!lifted.length) return tree;

  pruneEmpty(next);
  return lifted.reduce((acc, filter) => addExclusion(acc, filter), next);
}

/**
 * Set what a group's own terms mean: `any` (one of them is enough) or `all`
 * (every one has to appear).
 *
 * Split and merge restructure the query; this changes what one group asserts,
 * which is the far more common thing to want: only 38% of real groups can be
 * split or merged at all, so without this most groups had no way to change
 * their logic.
 */
export function setGroupJoiner(
  tree: YukkaQuery,
  group: QbGroup,
  joiner: QbJoiner,
): YukkaQuery {
  if (!group.path.length) return tree;
  const operator = joiner === 'all' ? 'and' : 'or';
  const next = cloneTree(tree);
  const node = nodeAt(next, group.path);

  // A bag whose terms are spread over several nodes has to become one node
  // first. Setting the operator on the parent alone would read as
  // `and(or(a, b), or(c))` — "a or b, and c" — and setting it everywhere would
  // split the one bag the user is looking at into two cards.
  const home = pathKey(group.path);
  if (group.terms.some((t) => pathKey(t.path) !== home)) {
    node.filters = group.terms.map((term) => ({
      ...(nodeAt(next, term.path).filters ?? [])[term.index],
    }));
    const emptied = new Set(
      group.terms.map((t) => pathKey(t.path)).filter((k) => k !== home),
    );
    node.groups = (node.groups ?? []).filter(
      (_, i) => !emptied.has(pathKey([...group.path, i])),
    );
  }

  node.operator = operator;
  return next;
}

/**
 * Set how the top-level blocks combine — the layer a referenced query sits on.
 *
 * Plain when there is nothing else at the root: flip its operator. With
 * exclusions present it has to restructure instead, because an all-negated node
 * under an OR excludes nothing:
 *
 *   and(g1, g2, NOT x)   →  and( or(g1, g2), NOT x )   asking for "any one"
 *   and( or(g1, g2), NOT x )  →  and(g1, g2, NOT x)    asking for "every"
 *
 * The exclusions stay AND-ed at the root either way, which is the only place
 * they work. Referenced artifacts stay at the root too — the wire format has no
 * place for them inside a node — so they remain AND-ed, and the artifact
 * separator says so.
 */
export function setTopJoiner(
  tree: YukkaQuery,
  joiner: 'and' | 'or',
): YukkaQuery {
  const next = cloneTree(tree);
  const children = next.groups ?? [];
  const excluded = children.filter((child) => isExclusionSubtree(child));
  const content = children.filter((child) => !isExclusionSubtree(child));
  // Exactly derive's unwrap rule: a single OR child *and* no included filters of
  // the root's own, or the root's filters would be a block the wrapper hides.
  const wrapper =
    content.length === 1 &&
    operatorOf(content[0]) === 'or' &&
    !!excluded.length &&
    !(next.filters ?? []).some((f) => !f.negated)
      ? content[0]
      : null;

  if (joiner === 'or') {
    if (wrapper || !excluded.length) {
      // Nothing in the way: the root itself can say it.
      if (!wrapper) next.operator = 'or';
      return next;
    }
    const included = (next.filters ?? []).filter((f) => !f.negated);
    next.filters = (next.filters ?? []).filter((f) => f.negated);
    next.groups = [
      {
        operator: 'or',
        slop: DEFAULT_SLOP,
        filters: included,
        groups: content,
      },
      ...excluded,
    ];
    return next;
  }

  if (wrapper) {
    // Put the alternatives back at the root, where AND joins them.
    const inner = wrapper.filters ?? [];
    next.groups = [
      ...(inner.length ? [makeTermsGroup(inner)] : []),
      ...(wrapper.groups ?? []),
      ...excluded,
    ];
  }
  next.operator = 'and';
  return next;
}

/**
 * Embed a saved query or portfolio: one more thing the query must match. The
 * server appends each one as another child of the root, so there is no position
 * to choose — it lives in the root's own array.
 */
export function addArtifact(
  tree: YukkaQuery,
  kind: QbArtifactKind,
  id: string,
  name: string,
  negated = false,
): YukkaQuery {
  const key = kind === 'query' ? 'referenced_queries' : 'referenced_portfolios';
  const list = tree[key] ?? [];
  if (list.some((a) => a.artifact_id === id && !!a.negated === negated)) {
    return tree;
  }
  const next = cloneTree(tree);
  next[key] = [...list, { artifact_id: id, negated, name }];
  return next;
}

export function removeArtifact(
  tree: YukkaQuery,
  artifact: QbArtifactRef,
): YukkaQuery {
  const key =
    artifact.kind === 'query' ? 'referenced_queries' : 'referenced_portfolios';
  const next = cloneTree(tree);
  next[key] = (next[key] ?? []).filter((_, i) => i !== artifact.index);
  return next;
}
